import { useState } from "react";
import { toast } from "sonner";
import type { CommandEndpoint } from "@shared-core/data/object-ui-adapter";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import type {
  Command,
  CommandResult,
} from "@shared-core/domain/object-ui/contract";
import { notifyObjectUiChanged } from "@shared-core/domain/object-ui/hooks";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { isQuietDryRun } from "@shared-core/domain/object-ui/setting-editor";
import type { Setting } from "@shared-core/domain/object-ui/settings";
import { Switch } from "../primitives/switch";
import type {
  CustomWidgetRegistry,
  KnownEntities,
} from "./command-shell/types";
import { SettingEditButton } from "./SettingEditPanel";

/**
 * A record fact a command sets, shown as a
 * setting. A value has an edit button beside it that opens a small panel for the command's one field (the command's
 * dialog when the panel cannot hold it); an on/off fact is a switch that applies at once when its dry run has nothing
 * to say, and opens the command when it has (a check, a choice, something to confirm) or when the command is
 * destructive.
 */
export function SettingFact({
  label,
  value,
  isYesNo,
  setting,
  widgets = {},
  needsAttention = false,
  onOpen,
}: {
  label: string;
  value: string;
  /** The fact is a plain yes/no, so its switch reads On or Off; otherwise it keeps its own words (Open, Blocked). */
  isYesNo: boolean;
  setting: Setting;
  /** The shell's custom widgets: a command with one is edited in its dialog, never the panel. */
  widgets?: CustomWidgetRegistry;
  /** An empty setting that holds the record up (its command leads): its picker is drawn in the amber attention tone. */
  needsAttention?: boolean;
  /** Open the command in the shell, starting from this body (and the names the panel showed for what it picked). */
  onOpen: (
    command: Command,
    initialBody?: Record<string, unknown>,
    knownEntities?: KnownEntities,
  ) => void;
}) {
  const adapter = useObjectUiAdapter();
  const index = useObjectManifest();
  const [isBusy, setBusy] = useState(false);
  const saved = (result: CommandResult) => {
    toast.success(result.summary);
    notifyObjectUiChanged();
  };

  if (setting.kind === "edit") {
    const [first, ...rest] = setting.commands;
    // A setting with no value yet is a visible picker, not a dash beside a pencil.
    const isEmpty = value === "–";
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
        {!(isEmpty && first) && <span>{value}</span>}
        {first && (
          <SettingEditButton
            label={label}
            currentValue={value}
            {...(isEmpty ? { placeholder: "Choose…", needsAttention } : {})}
            command={first}
            widgets={widgets}
            onOpen={onOpen}
            onSaved={saved}
          />
        )}
        {rest
          .filter((c) => c.enabled)
          .map((c) => (
            <button
              key={c.name}
              type="button"
              className="text-xs font-normal text-primary underline-offset-2 hover:underline"
              onClick={() => onOpen(c)}
            >
              {c.label}
            </button>
          ))}
      </span>
    );
  }

  const next = setting.isOn ? setting.turnOff : setting.turnOn;
  const toggle = async () => {
    if (!next) return;
    const declared = index.commandOf(next.command.name);
    const endpoint: CommandEndpoint = {
      command: declared,
      href: next.command.href,
      dryRunHref: next.command.dry_run_href,
    };
    if (declared.destructive) return onOpen(next.command, next.body);
    setBusy(true);
    try {
      const dryRun = await adapter.dryRun(endpoint, next.body);
      if (!isQuietDryRun(dryRun)) return onOpen(next.command, next.body);
      const hasRequestId =
        declared.request_schema?.properties?.request_id !== undefined;
      const result = await adapter.commit(endpoint, {
        ...next.body,
        ...(dryRun.guard ?? {}),
        ...(hasRequestId ? { request_id: newUuid() } : {}),
      });
      saved(result);
    } catch {
      console.warn("object_ui.setting.toggle_failed", {
        command: declared.id,
      });
      // The shell shows why, in the command's own words.
      onOpen(next.command, next.body);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <Switch
        checked={setting.isOn}
        disabled={isBusy || !next || !next.command.enabled}
        aria-label={label}
        title={
          next && !next.command.enabled
            ? (next.command.reason ?? undefined)
            : undefined
        }
        onCheckedChange={() => void toggle()}
      />
      <span>{isYesNo ? (setting.isOn ? "On" : "Off") : value}</span>
    </span>
  );
}
